import { useState, useEffect } from "react";
import { 
  Plus, Edit, Trash2, ExternalLink, Pin, PinOff, Eye, EyeOff,
  Globe, Newspaper, BookOpen, Mic, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCE_TYPES = [
  { id: "article", name: "Article", icon: Newspaper },
  { id: "publication", name: "Publication", icon: BookOpen },
  { id: "news", name: "News", icon: Globe },
  { id: "media", name: "Media/Interview", icon: Mic }
];

const ResourcesManager = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const { getAuthHeader } = useAuth();

  const [formData, setFormData] = useState({
    title: "",
    source: "",
    url: "",
    publication_date: "",
    description: "",
    resource_type: "article"
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/resources/admin`, {
        headers: getAuthHeader()
      });
      setResources(response.data.resources || []);
    } catch (error) {
      console.error("Error fetching resources:", error);
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      source: "",
      url: "",
      publication_date: "",
      description: "",
      resource_type: "article"
    });
    setEditingResource(null);
  };

  const openEditor = (resource = null) => {
    if (resource) {
      setEditingResource(resource);
      setFormData({
        title: resource.title || "",
        source: resource.source || "",
        url: resource.url || "",
        publication_date: resource.publication_date?.split("T")[0] || "",
        description: resource.description || "",
        resource_type: resource.resource_type || "article"
      });
    } else {
      resetForm();
    }
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.source || !formData.url) {
      toast.error("Title, source, and URL are required");
      return;
    }

    // Validate URL
    try {
      new URL(formData.url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setSaving(true);
    try {
      if (editingResource) {
        await axios.patch(`${API}/resources/admin/${editingResource.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Resource updated successfully!");
      } else {
        await axios.post(`${API}/resources/admin`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Resource added successfully!");
      }
      setShowEditor(false);
      resetForm();
      fetchResources();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to save resource";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (resource) => {
    if (!window.confirm(`Are you sure you want to delete "${resource.title}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/resources/admin/${resource.id}`, {
        headers: getAuthHeader()
      });
      toast.success("Resource deleted");
      fetchResources();
    } catch (error) {
      toast.error("Failed to delete resource");
    }
  };

  const togglePin = async (resource) => {
    try {
      await axios.post(`${API}/resources/admin/${resource.id}/toggle-pin`, null, {
        headers: getAuthHeader()
      });
      toast.success(resource.is_pinned ? "Resource unpinned" : "Resource pinned to top");
      fetchResources();
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  const toggleApprove = async (resource) => {
    try {
      await axios.post(`${API}/resources/admin/${resource.id}/toggle-approve`, null, {
        headers: getAuthHeader()
      });
      toast.success(resource.is_approved ? "Resource hidden" : "Resource approved");
      fetchResources();
    } catch (error) {
      toast.error("Failed to update approval status");
    }
  };

  const getTypeIcon = (type) => {
    const found = RESOURCE_TYPES.find(t => t.id === type);
    return found ? found.icon : Globe;
  };

  // Stats
  const approvedCount = resources.filter(r => r.is_approved).length;
  const pinnedCount = resources.filter(r => r.is_pinned).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-brand/10">
              <Globe className="h-5 w-5 text-brand" />
            </div>
            <div>
              <p className="text-2xl font-bold">{resources.length}</p>
              <p className="text-sm text-muted-foreground">Total Resources</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <Eye className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{approvedCount}</p>
              <p className="text-sm text-muted-foreground">Visible</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <Pin className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pinnedCount}</p>
              <p className="text-sm text-muted-foreground">Pinned</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Add articles, publications, and media mentions featuring Marshalee Valentine
        </p>
        <Button 
          onClick={() => openEditor()}
          className="bg-brand hover:bg-brand-dark"
          data-testid="add-resource-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Resource
        </Button>
      </div>

      {/* Resources List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">External Resources</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand" />
              <p className="text-muted-foreground mt-2">Loading resources...</p>
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Resources Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add links to articles, publications, and media appearances.
              </p>
              <Button onClick={() => openEditor()} className="bg-brand hover:bg-brand-dark">
                <Plus className="h-4 w-4 mr-2" />
                Add First Resource
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {resources.map((resource) => {
                const TypeIcon = getTypeIcon(resource.resource_type);
                return (
                  <div 
                    key={resource.id} 
                    className={`flex items-start gap-4 p-4 border rounded-lg hover:bg-slate-50 ${
                      !resource.is_approved ? 'opacity-60 bg-slate-50' : ''
                    } ${resource.is_pinned ? 'border-amber-300 bg-amber-50/50' : ''}`}
                    data-testid={`resource-row-${resource.id}`}
                  >
                    <div className="p-2 rounded-full bg-slate-100">
                      <TypeIcon className="h-4 w-4 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <a 
                          href={resource.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-semibold text-brand hover:underline truncate"
                        >
                          {resource.title}
                        </a>
                        {resource.is_pinned && (
                          <Pin className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        )}
                        {!resource.is_approved && (
                          <Badge className="bg-slate-100 text-slate-600">Hidden</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {resource.source}
                        {resource.publication_date && ` • ${new Date(resource.publication_date).toLocaleDateString()}`}
                      </p>
                      {resource.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{resource.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => window.open(resource.url, '_blank')}
                        title="Open Link"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditor(resource)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => togglePin(resource)}
                        title={resource.is_pinned ? "Unpin" : "Pin to top"}
                      >
                        {resource.is_pinned ? (
                          <PinOff className="h-4 w-4 text-amber-600" />
                        ) : (
                          <Pin className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => toggleApprove(resource)}
                        title={resource.is_approved ? "Hide" : "Show"}
                      >
                        {resource.is_approved ? (
                          <EyeOff className="h-4 w-4 text-slate-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-green-600" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(resource)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingResource ? "Edit Resource" : "Add External Resource"}</DialogTitle>
            <DialogDescription>
              Add a link to an article, publication, or media appearance
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Article or publication title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source">Source/Publication *</Label>
                <Input
                  id="source"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  placeholder="e.g., ISO Focus Magazine"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={formData.resource_type} 
                  onValueChange={(v) => setFormData({ ...formData, resource_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOURCE_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/article"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="publication_date">Publication Date (optional)</Label>
              <Input
                id="publication_date"
                type="date"
                value={formData.publication_date}
                onChange={(e) => setFormData({ ...formData, publication_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the resource"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-brand hover:bg-brand-dark"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingResource ? "Update" : "Add Resource"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourcesManager;
